import { useCallback, useMemo, useState } from 'react';
import { EmailCampaign } from 'shared/types/graphql';
import { availableSegments, availableSegmentsValues } from '../constants/conditionalSegments.constants';
import {
  ClinicConditionalSegment,
  ConditionProps,
  EmailCampaignSegmentDefinition,
  SegmentDefinition,
} from '../types/conditionalSegments.types';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';

const today = new Date();

export type useConditionalSegmentsReturnType = {
  conditions: {
    condition: ClinicConditionalSegment;
    segmentDefinition: unknown;
    Component: (props: ConditionProps) => JSX.Element;
    displayValidation: boolean;
    additionalSegmentProps?: Record<string, unknown>;
  }[];
  addCondition: (conditionName: ClinicConditionalSegment) => void;
  updateConditionValue: (conditionName: ClinicConditionalSegment, value: unknown) => void;
  removeCondition: (conditionName: ClinicConditionalSegment) => void;
  getSegmentsDefinition: () => EmailCampaignSegmentDefinition;
  validateSegments: () => boolean;
  hasUISegments: boolean;
  hasStoredSegments: boolean;
};

export const useConditionalSegments = (
  campaign?: EmailCampaign,
  additionalSegmentProps?: Record<string, unknown>,
): useConditionalSegmentsReturnType => {
  // Hold conditions and their corresponding values for each component
  const [_conditions, setConditions] = useState<Record<ClinicConditionalSegment, unknown>>(() => {
    // Initially there are no segments
    const res = {
      appointmentDate: { appointmentGte: subMonths(today, 18), appointmentLte: addMonths(today, 18) },
    } as Record<ClinicConditionalSegment, unknown>;

    // Load the campaign values
    if (campaign?.segmentArgs?.conditionalSegments) {
      const definitions: SegmentDefinition[] = campaign?.segmentArgs.conditionalSegments;
      for (const { segmentId, definitionVariables } of definitions) {
        const segment = availableSegmentsValues.find((availableSegment) => availableSegment.segmentId === segmentId);

        // Return only valid available segments and parse their campaign value using its definition
        if (segment) {
          const conditionValue = segment.parser
            ? segment.parser(definitionVariables.staticVariables)
            : definitionVariables.staticVariables;
          res[segment.name] = conditionValue;
        }
      }
    }

    return res;
  });

  const [displayConditionsError, setDisplayConditionsError] = useState<Record<ClinicConditionalSegment, boolean>>(
    {} as Record<ClinicConditionalSegment, boolean>,
  );

  // Resulting object that contains the relevant information such as
  // What to render, validation, current values
  const conditions = useMemo(() => {
    return Object.entries(_conditions).map((entry) => {
      const [condition, segmentDefinition] = entry as [ClinicConditionalSegment, unknown];
      const { component: Component, hasValidData } = availableSegments[condition];

      return {
        condition,
        segmentDefinition,
        Component,
        hasValidData,
        displayValidation: !!displayConditionsError[condition],
        additionalSegmentProps,
      };
    });
  }, [_conditions, displayConditionsError, additionalSegmentProps]);

  const hasUISegments = useMemo(() => !!conditions.length, [conditions]);

  const hasStoredSegments = useMemo(() => Object.keys(campaign?.segmentArgs || {}).length > 0, [campaign?.segmentArgs]);

  // CRUD operators for condition state
  const addCondition = useCallback(
    (conditionName: ClinicConditionalSegment): void =>
      setConditions((currentConditions) => ({
        ...currentConditions,
        [conditionName]: availableSegments[conditionName].initialValues || {},
      })),
    [],
  );

  const removeCondition = useCallback((conditionName: ClinicConditionalSegment): void => {
    setConditions((currentConditions) => {
      const newConditions = { ...currentConditions };
      delete newConditions[conditionName];

      return newConditions;
    });

    // Clear errors
    setDisplayConditionsError((current) => ({ ...current, [conditionName]: false }));
  }, []);

  const updateConditionValue = useCallback((conditionName: ClinicConditionalSegment, value: unknown): void => {
    setConditions((currentConditions) => ({ ...currentConditions, [conditionName]: value }));

    // Clear errors on valid input
    setDisplayConditionsError((current) => {
      if (current[conditionName]) {
        const displayValidation = availableSegments[conditionName].hasValidData(value) === false;

        return { ...current, [conditionName]: displayValidation };
      }

      return { ...current };
    });
  }, []);

  // Used before trying to submit, letting each component know if they need to show their errors
  const validateSegments = (): boolean => {
    let errorCount = 0;
    for (const { condition: conditionName, hasValidData, segmentDefinition } of conditions) {
      if (hasValidData(segmentDefinition) === false) {
        errorCount++;
        setDisplayConditionsError((current) => ({ ...current, [conditionName]: true }));
      }
    }

    return errorCount === 0;
  };

  // Final segment to be stored in the campaign
  const getSegmentsDefinition = (): EmailCampaignSegmentDefinition => {
    // Formatter function, has no use outside this scope
    const buildSegmentDefinition = (segment: ClinicConditionalSegment, data: unknown): SegmentDefinition => {
      const { segmentId } = availableSegments[segment];

      return {
        segmentId,
        definitionVariables: {
          staticVariables: data,
        },
      };
    };

    const conditionalSegments = conditions.map(({ condition, segmentDefinition }) =>
      buildSegmentDefinition(condition, segmentDefinition),
    );

    return { conditionalSegments };
  };

  return {
    conditions,
    updateConditionValue,
    removeCondition,
    addCondition,
    getSegmentsDefinition,
    validateSegments,
    hasUISegments,
    hasStoredSegments,
  };
};
